import { Button, FormField, RadioGroup, UIHeading1, useDeviceType } from '@cian/ui-kit';
import { clsx } from 'clsx';

import * as styles from './DobroListing.css';
import { EStatus, IOffer } from 'shared/types/listing/listing';
import {
  formatArea,
  formatDate,
  formatLocation,
  formatNumber,
  formatObjectType,
  formatRoomsCount,
  formatStatus,
} from 'shared/utils/formatters';
import { ChangeEvent, useCallback } from 'react';
import { EMoscow } from 'shared/types/form';

export interface IDobroListingProps {
  region: EMoscow | null;
  setRegion: (value: EMoscow | null) => void;
  status: EStatus | null;
  setStatus: (status: EStatus | null) => void;
  offers: IOffer[];
  openModal: (offer: IOffer) => void;
  anyRegionCount: number;
  countByRegion: { [key: string]: number };
  anyStatusCount: number;
  countByStatus: { [key in EStatus]: number };
}

function formatCount(count: number) {
  return ` (${count})`;
}

export function DobroListing(props: IDobroListingProps) {
  const {
    offers,
    openModal,
    region,
    setRegion,
    status,
    setStatus,
    anyRegionCount,
    countByRegion,
    anyStatusCount,
    countByStatus,
  } = props;
  const deviceType = useDeviceType();

  const onRegionChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, region: EMoscow | '') => {
      setRegion(region === '' ? null : region);
    },
    [setRegion],
  );

  const onStatusChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, status: EStatus | '') => {
      setStatus(status === '' ? null : status);
    },
    [setStatus],
  );

  return (
    <div
      className={clsx(styles.page, {
        [styles.desktop]: deviceType === 'desktop',
        [styles.mobile]: deviceType !== 'desktop',
      })}
    >
      <div className={styles.heading}>
        <UIHeading1>Заявки</UIHeading1>
      </div>

      <div className={styles.form} data-name="DobroListing-form">
        <div data-name="DobroListing-form-region">
          <FormField label="Регион">
            <RadioGroup
              horizontal
              value={region || ''}
              onChange={onRegionChange}
              options={[
                { value: '', label: 'Любой' + formatCount(anyRegionCount) },
                {
                  value: EMoscow.Moscow,
                  label: formatLocation(EMoscow.Moscow) + formatCount(countByRegion[EMoscow.Moscow]),
                },
                {
                  value: EMoscow.MoscowRegion,
                  label: formatLocation(EMoscow.MoscowRegion) + formatCount(countByRegion[EMoscow.MoscowRegion]),
                },
                {
                  value: EMoscow.Other,
                  label: formatLocation(EMoscow.Other) + formatCount(countByRegion[EMoscow.Other]),
                },
              ]}
            />
          </FormField>
        </div>
        <div data-name="DobroListing-form-status">
          <FormField label="Статус">
            <RadioGroup
              horizontal
              value={status || ''}
              onChange={onStatusChange}
              options={[
                { value: '', label: 'Любой' + formatCount(anyStatusCount) },
                { value: EStatus.New, label: formatStatus(EStatus.New) + formatCount(countByStatus[EStatus.New]) },
                {
                  value: EStatus.Phone_not_available,
                  label:
                    formatStatus(EStatus.Phone_not_available) + formatCount(countByStatus[EStatus.Phone_not_available]),
                },
                {
                  value: EStatus.Canceled,
                  label: formatStatus(EStatus.Canceled) + formatCount(countByStatus[EStatus.Canceled]),
                },
                {
                  value: EStatus.Confirming,
                  label: formatStatus(EStatus.Confirming) + formatCount(countByStatus[EStatus.Confirming]),
                },
                { value: EStatus.Busy, label: formatStatus(EStatus.Busy) + formatCount(countByStatus[EStatus.Busy]) },
                {
                  value: EStatus.Available,
                  label: formatStatus(EStatus.Available) + formatCount(countByStatus[EStatus.Available]),
                },
              ]}
            />
          </FormField>
        </div>
      </div>

      {deviceType === 'desktop' ? (
        <DobroListingDesktop openModal={openModal} offers={offers} />
      ) : (
        <DobroListingMobile openModal={openModal} offers={offers} />
      )}
    </div>
  );
}

function DobroListingDesktop({ offers, openModal }: { offers: IOffer[]; openModal: (offer: IOffer) => void }) {
  return (
    <table className={styles.desktop_table} data-testid="DobroListingDesktop-table">
      <thead>
        <tr>
          <th>Статус</th>
          <th>Дата создания</th>
          <th>Тип жилья</th>
          <th>Адрес</th>
          <th>Площадь</th>
          <th>Кол-во комнат</th>
          <th>Кол-во гостей</th>
          <th>Кол-во спальных мест</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {offers.map((offer, index) => (
          <DobroListingDesktopItem key={index} offer={offer} openModal={openModal} />
        ))}
      </tbody>
    </table>
  );
}

function DobroListingDesktopItem({ offer, openModal }: { offer: IOffer; openModal: (offer: IOffer) => void }) {
  const onClick = useCallback(() => {
    openModal(offer);
  }, [openModal, offer]);

  return (
    <tr data-testid="DobroListingDesktopItem">
      <td data-testid="DobroListingDesktopItem-status">{formatStatus(offer.status)}</td>
      <td data-testid="DobroListingDesktopItem-createdAt">{formatDate(offer.createdAt)}</td>
      <td data-testid="DobroListingDesktopItem-objectType">{formatObjectType(offer.objectType)}</td>
      <td data-testid="DobroListingDesktopItem-address">
        <OfferLocation offer={offer} />
      </td>
      <td data-testid="DobroListingDesktopItem-area">{formatArea(offer.area)}</td>
      <td data-testid="DobroListingDesktopItem-roomsCount">{formatRoomsCount(offer.roomsCount)}</td>
      <td data-testid="DobroListingDesktopItem-guestsCount">{offer.guestsCount}</td>
      <td data-testid="DobroListingDesktopItem-bedsCount">{formatNumber(offer.bedsCount)}</td>
      <td>
        <Button onClick={onClick} theme="fill_secondary">
          Подробнее
        </Button>
      </td>
    </tr>
  );
}

function DobroListingMobile({ offers, openModal }: { offers: IOffer[]; openModal: (offer: IOffer) => void }) {
  return (
    <div>
      {offers.map((offer, index) => (
        <DobroListingMobileItem key={index} offer={offer} openModal={openModal} />
      ))}
    </div>
  );
}

function DobroListingMobileItem({ offer, openModal }: { offer: IOffer; openModal: (offer: IOffer) => void }) {
  const onClick = useCallback(() => {
    openModal(offer);
  }, [openModal, offer]);

  return (
    <div className={styles.mobile_card}>
      <table className={styles.mobile_card_table}>
        <tbody>
          <tr>
            <th>Статус</th>
            <td>{formatStatus(offer.status)}</td>
          </tr>
          <tr>
            <th>Дата создания</th>
            <td>{formatDate(offer.createdAt)}</td>
          </tr>
          <tr>
            <th>Тип жилья</th>
            <td>{formatObjectType(offer.objectType)}</td>
          </tr>
          <tr>
            <th>Адрес</th>
            <td>
              <OfferLocation offer={offer} />
            </td>
          </tr>
          <tr>
            <th>Площадь</th>
            <td>{formatArea(offer.area)}</td>
          </tr>
          <tr>
            <th>Кол-во комнат</th>
            <td>{formatRoomsCount(offer.roomsCount)}</td>
          </tr>
          <tr>
            <th>Кол-во гостей</th>
            <td>{offer.guestsCount}</td>
          </tr>
          <tr>
            <th>Кол-во спальных мест</th>
            <td>{formatNumber(offer.bedsCount)}</td>
          </tr>
        </tbody>
      </table>
      <Button theme="fill_secondary" fullWidth onClick={onClick}>
        Подробнее
      </Button>
    </div>
  );
}

function OfferLocation({ offer }: { offer: IOffer }) {
  return (
    <>
      Город: <span className={styles.cut_text}>{formatLocation(offer.location)}</span>
      <br />
      Адрес: <span className={styles.cut_text}>{offer.address}</span>
      <br />
      Этаж: {formatNumber(offer.floor)}
    </>
  );
}
